import { useEffect } from 'react';

/**
 * Publishes the keeper strip's MEASURED bottom edge as `--strip-bottom` (px) on the shell, for
 * the one tier whose rail has to hug the strip rather than sit under its worst case.
 *
 * Owner ruling 2026-09-19 (RULINGS "The short tier: the rail hugs the strip"): at `short`
 * (≥768 wide, ≤519 tall) there is no room for a 340 rail under a 168 worst-case strip reserve,
 * and pinning the rail to the bottom instead is what put a two-chip strip and the rail on top of
 * each other by ~90 px. `--strip-reserve` is a CONSTANT — the two-wrapped-chips worst case — which
 * every taller tier can afford to leave empty and this one cannot: with a one-chip strip (~49
 * tall, the common case) a constant reserve wastes 120 px of a 412-tall viewport. So this tier
 * reads the strip's real height instead. A `ResizeObserver` on the `<header class="hud">` keeps
 * the variable honest as chips wrap and unwrap; the token's declared default
 * (`tokens.component.css`) is `--strip-reserve`, which is what the rail reads with no HUD on the
 * route or before the first measurement lands — removing the inline property restores it.
 *
 * Set on `document.documentElement` rather than the shell element so `.nav-bar`, a sibling of the
 * strip, can read it without the two sharing a container — the same reason `--nav-active` lives
 * where it does.
 */
/** `key` re-arms the observer when the route changes (the wardrobe hides the strip, so its
 *  height there is 0 and the rail may rise). */
export function useStripBottom(key: string): void {
  useEffect(() => {
    const root = document.documentElement;
    const hud = document.querySelector<HTMLElement>('.hud');
    if (!hud || typeof ResizeObserver === 'undefined') {
      root.style.removeProperty('--strip-bottom');
      return;
    }
    const publish = () => {
      root.style.setProperty('--strip-bottom', `${Math.ceil(hud.getBoundingClientRect().bottom)}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(hud);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--strip-bottom');
    };
  }, [key]);
}
